import { z } from 'zod';

/**
 * Gatherable resource nodes of exterior scenes (PrimeraVersion.md §7). Ids are a public
 * contract (wire, database, i18n `nodes.<kind>`): never rename.
 * - `wood`: a pile of felled logs; `food`: a berry bush; `iron`: an ore vein.
 * - `cache`: the ruin's resource container, a mixed load with a long respawn.
 * - `stone`: an outcrop; `reeds`: a reed bed (straw and fiber); `mud`: a muddy bank; `resin`:
 *   a resinous pine (TI-27, plan C0).
 * - `pebbles`: loose stones and flint, picked up by hand (RES-GATHER RG-1). `wood` is now the loose
 *   branches of a pile, by hand too; trees give the wood.
 * - `tree`: a tree of the generator (RES-GATHER RG-3), never in a chunk's nodes: woken when someone
 *   fells it, its id its tile like any node's.
 * - `tall_grass`: a patch of tall grass, cut by hand for straw (RES-GATHER RG-1; the reeds give
 *   fiber).
 */
export const NODE_KINDS = [
  'wood',
  'food',
  'iron',
  'cache',
  'stone',
  'reeds',
  'mud',
  'resin',
  'pebbles',
  'tree',
  'tall_grass',
  // Orven's own materials, zone by zone (BZ-1): a zone's common good that sets it apart.
  'flax',
  'wicker',
  'seeds',
  'rubble',
  'clay',
  'oak_bark',
  'rock_moss',
  'lichen',
  // Breya's healing herbs (OH-5): Mada's fever, by hand.
  'herbs',
] as const;
export const NodeKindSchema = z.enum(NODE_KINDS);
export type NodeKind = z.infer<typeof NodeKindSchema>;

/**
 * Node id: `<x>,<y>` of its world tile (ADR 0031). Stable as long as the world keeps the node
 * in place, short on the wire, and its persistence key.
 */
export const NodeIdSchema = z.string().regex(/^-?\d{1,7},-?\d{1,7}$/);
export type NodeId = z.infer<typeof NodeIdSchema>;

/**
 * A tree planted on a world tile (RES-GATHER RG-13), as every client lays it on its map: in a
 * stump it names the species that grows back (RF-2); on free land a sapling until `grown`, then a
 * tree (PL-4).
 */
export const PlantingStateSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  species: z.string().min(1).max(32),
  grown: z.boolean(),
});
export type PlantingState = z.infer<typeof PlantingStateSchema>;

/** Availability of a node as players see it. */
export const NodeStateSchema = z.object({
  id: NodeIdSchema,
  /** Server game time when a depleted node comes back; null while available. */
  respawnAt: z.number().int().nonnegative().nullable(),
  /** A stump with a sapling planted in it (RES-GATHER RG-13): drawn as one, planted no more. */
  planted: z.literal(true).optional(),
});
export type NodeState = z.infer<typeof NodeStateSchema>;
